import {
  Briefcase,
  FileText,
  Palette,
  Building2,
  Plug,
  FolderPlus,
  Database,
  LayoutGrid,
  MessagesSquare,
  User,
  CreditCard,
  Settings,
} from 'lucide-react';
import type { NavigationItem } from '../types/SideMenu.types';

export type StartupNavSection =
  | 'home'
  | 'work-scopes'
  | 'discussions'
  | 'artifacts'
  | 'files'
  | 'data'
  | 'design-system'
  | 'startup-profile'
  | 'integrations';

/**
 * Build the startup-level sidebar nav items from a single `activeSection` and
 * per-section navigation callbacks. Each callback is optional; sections without a
 * callback render disabled.
 */
export const buildStartupNavItems = (opts: {
  activeSection: StartupNavSection;
  onNavigateToHome?: () => void;
  onNavigateToWorkScopes?: () => void;
  /** Startup-level Discussion Room (Atlas chat), separate from scope work. */
  onNavigateToDiscussions?: () => void;
  onNavigateToArtifacts?: () => void;
  onNavigateToFiles?: () => void;
  /** The startup's live data, users and data settings (User Story 598). */
  onNavigateToData?: () => void;
  onNavigateToDesignSystem?: () => void;
  onNavigateToStartupProfile?: () => void;
  onNavigateToIntegrations?: () => void;
  /**
   * Back to the startups list. Every other item in this nav is scoped to the
   * current startup, so this is the only gate to switch to a different one —
   * rendered at the bottom (just above the user footer) when provided.
   */
  onNavigateToStartups?: () => void;
  /** Optional translator (i18n). Falls back to English labels when omitted. */
  t?: (key: string) => string;
}): NavigationItem[] => {
  const tr = (key: string, fallback: string) => (opts.t ? opts.t(key) : fallback);
  return [
  // Home is hidden for now — users land directly on Work Scopes. Re-add this
  // item (and the `Home` icon import) to bring the Home tab back.
  {
    id: 'work-scopes',
    label: tr('nav.workScopes', 'Work Scopes'),
    icon: Briefcase,
    isActive: opts.activeSection === 'work-scopes',
    disabled: !opts.onNavigateToWorkScopes,
    type: 'navigation',
    testId: 'sidemenu-work-scopes',
    onClick: opts.onNavigateToWorkScopes,
  },
  {
    id: 'discussions',
    label: tr('nav.discussions', 'Discussions'),
    icon: MessagesSquare,
    isActive: opts.activeSection === 'discussions',
    disabled: !opts.onNavigateToDiscussions,
    type: 'navigation',
    testId: 'sidemenu-discussions',
    onClick: opts.onNavigateToDiscussions,
  },
  {
    id: 'artifacts',
    label: tr('nav.artifacts', 'Artifacts'),
    icon: FileText,
    isActive: opts.activeSection === 'artifacts',
    disabled: !opts.onNavigateToArtifacts,
    type: 'navigation',
    testId: 'sidemenu-artifacts',
    onClick: opts.onNavigateToArtifacts,
  },
  {
    id: 'files',
    label: tr('nav.knowledgeHub', 'Knowledge Hub'),
    icon: FolderPlus,
    isActive: opts.activeSection === 'files',
    disabled: !opts.onNavigateToFiles,
    type: 'navigation',
    testId: 'sidemenu-files',
    onClick: opts.onNavigateToFiles,
  },
  {
    id: 'data',
    label: tr('nav.data', 'Data'),
    icon: Database,
    isActive: opts.activeSection === 'data',
    disabled: !opts.onNavigateToData,
    type: 'navigation',
    testId: 'sidemenu-data',
    onClick: opts.onNavigateToData,
  },
  {
    id: 'design-system',
    label: tr('nav.designSystem', 'Design system'),
    icon: Palette,
    isActive: opts.activeSection === 'design-system',
    disabled: !opts.onNavigateToDesignSystem,
    type: 'navigation',
    testId: 'sidemenu-design-system',
    onClick: opts.onNavigateToDesignSystem,
  },
  {
    id: 'startup-profile',
    label: tr('nav.startupProfile', 'Startup Profile'),
    icon: Building2,
    isActive: opts.activeSection === 'startup-profile',
    disabled: !opts.onNavigateToStartupProfile,
    type: 'navigation',
    testId: 'sidemenu-startup-profile',
    onClick: opts.onNavigateToStartupProfile,
  },
  {
    id: 'integrations',
    label: tr('nav.integrations', 'Integrations'),
    icon: Plug,
    isActive: opts.activeSection === 'integrations',
    disabled: !opts.onNavigateToIntegrations,
    type: 'navigation',
    testId: 'sidemenu-integrations',
    onClick: opts.onNavigateToIntegrations,
  },
  // My Startups sits at the bottom of the scoped nav (just above the user
  // footer) — the gate back to the startups list to switch startup.
  ...(opts.onNavigateToStartups
    ? [
        {
          id: 'startups',
          label: tr('nav.myStartups', 'My Startups'),
          icon: LayoutGrid,
          isActive: false,
          type: 'navigation' as const,
          testId: 'sidemenu-my-startups',
          onClick: opts.onNavigateToStartups,
          // Pinned to the bottom of the startup sidebar (above the user footer).
          pinBottom: true,
        },
      ]
    : []),
  ];
};

export type AccountNavSection = 'startups' | 'profile' | 'billing' | 'app-settings';

/**
 * Build the account-level sidebar nav items — My Startups / Profile / Billing /
 * App Settings. Used on root pages that are NOT nested under a startup
 * (`/profile`, `/billing`, `/app-settings`): the startup-scoped nav (Work
 * Scopes, Artifacts, …) is meaningless there, so these pages show account nav
 * instead.
 */
export const buildAccountNavItems = (opts: {
  activeSection: AccountNavSection;
  onNavigateToStartups?: () => void;
  onNavigateToProfile?: () => void;
  onNavigateToBilling?: () => void;
  onNavigateToAppSettings?: () => void;
  /** Optional translator (i18n). Falls back to English labels when omitted. */
  t?: (key: string) => string;
}): NavigationItem[] => {
  const tr = (key: string, fallback: string) => (opts.t ? opts.t(key) : fallback);
  return [
    {
      id: 'startups',
      label: tr('nav.myStartups', 'My Startups'),
      icon: LayoutGrid,
      isActive: opts.activeSection === 'startups',
      disabled: !opts.onNavigateToStartups,
      type: 'navigation',
      testId: 'sidemenu-startups',
      onClick: opts.onNavigateToStartups,
    },
    {
      id: 'profile',
      label: tr('nav.profile', 'Profile'),
      icon: User,
      isActive: opts.activeSection === 'profile',
      disabled: !opts.onNavigateToProfile,
      type: 'navigation',
      testId: 'sidemenu-profile',
      onClick: opts.onNavigateToProfile,
    },
    {
      id: 'billing',
      label: tr('nav.billing', 'Billing'),
      icon: CreditCard,
      isActive: opts.activeSection === 'billing',
      disabled: !opts.onNavigateToBilling,
      type: 'navigation',
      testId: 'sidemenu-billing',
      onClick: opts.onNavigateToBilling,
    },
    {
      id: 'app-settings',
      label: tr('nav.appSettings', 'App Settings'),
      icon: Settings,
      isActive: opts.activeSection === 'app-settings',
      disabled: !opts.onNavigateToAppSettings,
      type: 'navigation',
      testId: 'sidemenu-app-settings',
      onClick: opts.onNavigateToAppSettings,
    },
  ];
};
